import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch, ApiError } from '../lib/api';
import type { CaseSummary, TaskPriority, TaskSummary } from '../types';

const PRIORITIES: TaskPriority[] = ['low', 'medium', 'high'];

export function TaskModal({
  initial,
  onSaved,
  onClose,
  onDeleted,
}: {
  initial?: TaskSummary | null;
  onSaved: () => void;
  onClose: () => void;
  onDeleted?: () => void;
}) {
  const { t } = useTranslation(['tasks', 'common']);
  const editing = !!initial;

  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [dueAt, setDueAt] = useState(() => (initial?.dueAt ? toLocalInput(initial.dueAt) : ''));
  const [priority, setPriority] = useState<TaskPriority>(initial?.priority ?? 'medium');
  const [caseId, setCaseId] = useState(initial?.caseId ?? '');
  const [cases, setCases] = useState<CaseSummary[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<{ items: CaseSummary[] }>('/api/cases')
      .then((d) => setCases(d.items))
      .catch(() => setCases([]));
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const body = {
        title: title.trim(),
        description: description.trim() || null,
        dueAt: dueAt ? new Date(dueAt).toISOString() : null,
        priority,
        caseId: caseId || null,
      };
      if (editing && initial) {
        await apiFetch(`/api/tasks/${initial.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
      } else {
        await apiFetch('/api/tasks', {
          method: 'POST',
          body: JSON.stringify(body),
        });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.code : err instanceof Error ? err.message : 'unknown');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!editing || !initial) return;
    if (!confirm(t('form.delete_confirm', { title: initial.title }))) return;
    setBusy(true);
    try {
      await apiFetch(`/api/tasks/${initial.id}`, { method: 'DELETE' });
      onDeleted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'delete_failed');
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="form-card modal-card" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="form-card-header">
            <div style={{ flex: 1 }}>
              <div className="form-preview-name">
                {editing ? t('form.title_edit') : t('form.title_new')}
              </div>
            </div>
            <button
              type="button"
              className="modal-close-btn"
              onClick={onClose}
              aria-label={t('common:actions.close')}
            >
              ×
            </button>
          </div>

          <div className="form-section">
            <div className="field-grid" style={{ gridTemplateColumns: '1fr' }}>
              <div className="field">
                <label>{t('form.field_title')} <span className="req">*</span></label>
                <input value={title} onChange={(e) => setTitle(e.target.value)} required autoFocus />
              </div>
              <div className="field">
                <label>{t('form.field_description')}</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} />
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="field-grid">
              <div className="field">
                <label>{t('form.field_due')}</label>
                <input
                  type="datetime-local"
                  value={dueAt}
                  onChange={(e) => setDueAt(e.target.value)}
                />
              </div>
              <div className="field">
                <label>{t('form.field_priority')}</label>
                <select value={priority} onChange={(e) => setPriority(e.target.value as TaskPriority)}>
                  {PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {t(`priorities.${p}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <label>{t('form.field_case')}</label>
                <select value={caseId} onChange={(e) => setCaseId(e.target.value)}>
                  <option value="">{t('form.no_case')}</option>
                  {cases.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.caseNumber} {c.client?.name ? `· ${c.client.name}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {error && (
            <div className="form-section">
              <div className="signin-error">{error}</div>
            </div>
          )}

          <div className="form-footer">
            <button type="submit" className="btn-save" disabled={busy}>
              {busy ? t('common:actions.loading_dots') : t('form.submit')}
            </button>
            <button type="button" className="btn-cancel" onClick={onClose} disabled={busy}>
              {t('common:actions.cancel')}
            </button>
            {editing && onDeleted && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={busy}
                className="btn-danger"
                style={{ marginInlineStart: 'auto' }}
              >
                {t('common:actions.delete')}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}
